/**
 * useCoursesStore - Zustand store for caching courses data across the app.
 *
 * Phase 2 Optimization: Implements smart caching with invalidation logic.
 * Components consume cached data instead of making individual API calls.
 */
import { create } from 'zustand';
import type { Id } from '../../convex/_generated/dataModel';

export interface Course {
  _id: Id<"courses">;
  _creationTime: number;
  ownerId: string;
  title: string;
  courseCode?: string;
  professorName?: string;
  yearSemester?: string;
  additionalInfo?: string;
  isArchived?: boolean;
  archivedAt?: number;
  documentCount?: number;
  subfolderCount?: number;
}

interface CoursesStore {
  courses: Course[] | undefined;
  isLoading: boolean;
  lastFetch: number;
  setCourses: (courses: Course[] | undefined) => void;
  setIsLoading: (loading: boolean) => void;
  shouldRefetch: () => boolean;
  invalidate: () => void;
}

const CACHE_DURATION = 60000; // 1 minute cache

export const useCoursesStore = create<CoursesStore>((set, get) => ({
  courses: undefined,
  isLoading: true,
  lastFetch: 0,

  setCourses: (courses) => set({
    courses,
    isLoading: false,
    lastFetch: Date.now()
  }),

  setIsLoading: (isLoading) => set({ isLoading }),

  // Smart invalidation: only refetch if cache is stale
  shouldRefetch: () => {
    const { lastFetch } = get();
    return Date.now() - lastFetch > CACHE_DURATION;
  },

  // Force invalidation (e.g., after creating/deleting a course)
  invalidate: () => set({ lastFetch: 0 })
}));
